import { useMemo, type CSSProperties, type Dispatch, type SetStateAction } from 'react';
import { Input } from '@vd/ui';
import { Check, Search } from 'lucide-react';
import { FixedSizeList } from 'react-window';
import type { Project } from '@vd/types';
import styles from './ProjectAccessPicker.module.css';
import { ProjectMark } from '../projects/ProjectMark';

/**
 * Controlled access-mode + project picker. Two consumers today:
 *
 *  - ProjectAccessModal: per-member access changes after a user is
 *    already in the company.
 *  - MembersTab invite modal: per-batch access policy at invite-creation
 *    time, so a restricted invitee never has a window where they can
 *    see every project.
 *
 * Both consumers pass projects + the controlled state. Keeping it
 * controlled means each parent owns reset semantics (modal close,
 * inviter switching tabs) without this component carrying internal
 * useEffect-reset logic that has to handle both cases.
 */

const ITEM_ROW_HEIGHT = 36;
const VIRTUALIZE_THRESHOLD = 50;
const AVAILABLE_LIST_MAX_HEIGHT = 280;

export interface ProjectAccessPickerProps {
  accessMode: 'all_projects' | 'restricted';
  setAccessMode: Dispatch<SetStateAction<'all_projects' | 'restricted'>>;
  selected: Set<string>;
  setSelected: Dispatch<SetStateAction<Set<string>>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  projects: Project[];
  /** Short noun for radio hints + summary line. "Jane", "Members in this batch", etc. */
  subjectLabel: string;
  /** Override summary copy for callers (the modal renders its own footer). */
  hideSummary?: boolean;
}

export function ProjectAccessPicker({
  accessMode,
  setAccessMode,
  selected,
  setSelected,
  search,
  setSearch,
  projects,
  subjectLabel,
  hideSummary,
}: ProjectAccessPickerProps) {
  const { selectedProjects, availableProjects, totalMatches } = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matchesSearch = (p: Project) => !q || p.name.toLowerCase().includes(q);
    const selectedList: Project[] = [];
    const availableList: Project[] = [];
    let matchCount = 0;
    for (const p of projects) {
      const m = matchesSearch(p);
      if (selected.has(p.id)) {
        selectedList.push(p);
        if (m) matchCount++;
      } else if (m) {
        availableList.push(p);
        matchCount++;
      }
    }
    selectedList.sort((a, b) => a.name.localeCompare(b.name));
    availableList.sort((a, b) => a.name.localeCompare(b.name));
    return { selectedProjects: selectedList, availableProjects: availableList, totalMatches: matchCount };
  }, [projects, selected, search]);

  const toggle = (id: string) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAllVisible = () => {
    setSelected((s) => {
      const next = new Set(s);
      for (const p of availableProjects) next.add(p.id);
      return next;
    });
  };
  const clearAll = () => setSelected(new Set());

  const renderItem = (p: Project, style?: CSSProperties) => {
    const isSelected = selected.has(p.id);
    return (
      <button
        type="button"
        key={p.id}
        className={`${styles.item} ${isSelected ? styles.itemSelected : ''}`}
        onClick={() => toggle(p.id)}
        role="option"
        aria-selected={isSelected}
        style={style}
      >
        <ProjectMark project={p} size={16} />
        <span className={styles.name}>{p.name}</span>
        {isSelected && <Check size={14} className={styles.checkMark} />}
      </button>
    );
  };

  return (
    <>
      <label className={styles.radioRow}>
        <input
          type="radio"
          name="access-mode"
          checked={accessMode === 'all_projects'}
          onChange={() => setAccessMode('all_projects')}
        />
        <div>
          <div className={styles.radioLabel}>All projects</div>
          <div className={styles.radioHint}>{subjectLabel} sees every project in this workspace.</div>
        </div>
      </label>

      <label className={styles.radioRow}>
        <input
          type="radio"
          name="access-mode"
          checked={accessMode === 'restricted'}
          onChange={() => setAccessMode('restricted')}
        />
        <div>
          <div className={styles.radioLabel}>Specific projects only</div>
          <div className={styles.radioHint}>{subjectLabel} sees only the projects you select below.</div>
        </div>
      </label>

      {accessMode === 'restricted' && (
        <div className={styles.pickerWrap}>
          <div className={styles.searchRow}>
            <Search size={14} className={styles.searchIcon} />
            <Input
              placeholder="Search projects..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className={styles.bulkRow}>
            <button
              type="button"
              className={styles.bulkBtn}
              onClick={selectAllVisible}
              disabled={availableProjects.length === 0}
            >
              {search.trim()
                ? `Select all matching (${availableProjects.length})`
                : `Select all (${availableProjects.length})`}
            </button>
            <span className={styles.bulkSep} aria-hidden>·</span>
            <button
              type="button"
              className={styles.bulkBtn}
              onClick={clearAll}
              disabled={selected.size === 0}
            >
              Clear selection
            </button>
            <span className={styles.bulkCount}>
              {search.trim() ? `${totalMatches} of ${projects.length} match` : `${projects.length} projects`}
            </span>
          </div>

          {selectedProjects.length > 0 && (
            <div className={styles.group}>
              <div className={styles.groupHeader}>
                <span className={styles.groupTitle}>Selected ({selectedProjects.length})</span>
              </div>
              <div className={styles.groupList} role="listbox" aria-label="Selected projects">
                {selectedProjects.map((p) => renderItem(p))}
              </div>
            </div>
          )}

          <div className={styles.group}>
            <div className={styles.groupHeader}>
              <span className={styles.groupTitle}>Available ({availableProjects.length})</span>
            </div>
            {availableProjects.length === 0 ? (
              <div className={styles.empty}>
                {search.trim()
                  ? 'No more projects match this search.'
                  : selected.size === projects.length
                  ? 'All projects in this workspace are selected.'
                  : 'No projects in this workspace yet.'}
              </div>
            ) : availableProjects.length > VIRTUALIZE_THRESHOLD ? (
              <FixedSizeList
                height={AVAILABLE_LIST_MAX_HEIGHT}
                itemCount={availableProjects.length}
                itemSize={ITEM_ROW_HEIGHT}
                width="100%"
                className={styles.virtualList}
                itemData={{ selected }}
              >
                {({ index, style }) => {
                  const p = availableProjects[index];
                  if (!p) return null;
                  return renderItem(p, style);
                }}
              </FixedSizeList>
            ) : (
              <div className={styles.groupList} role="listbox" aria-label="Available projects">
                {availableProjects.map((p) => renderItem(p))}
              </div>
            )}
          </div>

          {!hideSummary && (
            <div className={styles.summary}>
              {selected.size === 0
                ? 'Select at least one project.'
                : `${subjectLabel} will see ${selected.size} project${selected.size === 1 ? '' : 's'}.`}
            </div>
          )}
        </div>
      )}
    </>
  );
}
